import type { Tab } from './index';
import { esc, setTableState } from '../utils';
import { fetchSheet } from '../worker';
import { writeToSheet } from '../sheets';
import { showToast } from '../toast';
import { store } from '../state';

interface IpoRow {
  _id:          string | number;
  Company:      string;
  Ticker?:      string;
  Exchange?:    string;
  ExpectedDate?:string;
  PriceRange?:  string;
  Shares?:      string;
  Valuation?:   string;
  Status:       string;
  Sector?:      string;
  Notes?:       string;
}

interface FieldDef {
  key:      string;
  label:    string;
  type:     'text' | 'select' | 'textarea';
  required: boolean;
  options?: string[];
}

const FIELDS: FieldDef[] = [
  { key:'Company',       label:'Company',        type:'text',     required:true  },
  { key:'Ticker',        label:'Ticker',         type:'text',     required:false },
  { key:'Exchange',      label:'Exchange',        type:'text',     required:false },
  { key:'ExpectedDate',  label:'Expected Date',  type:'text',     required:false },
  { key:'PriceRange',    label:'Price Range',    type:'text',     required:false },
  { key:'Shares',        label:'Shares Offered', type:'text',     required:false },
  { key:'Valuation',     label:'Valuation',      type:'text',     required:false },
  { key:'Status',        label:'Status',         type:'select',   required:true,
    options:['Pipeline','Upcoming','Completed','Withdrawn'] },
  { key:'Sector',        label:'Sector',         type:'text',     required:false },
  { key:'Notes',         label:'Notes',          type:'textarea', required:false },
];

const STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  Completed: { bg:'rgba(46,213,115,0.12)', color:'#00ff9d', border:'rgba(46,213,115,0.3)' },
  Upcoming:  { bg:'rgba(255,184,48,0.12)', color:'#fbbf24', border:'rgba(255,184,48,0.3)' },
  Pipeline:  { bg:'rgba(0,212,255,0.12)',  color:'#00d4ff', border:'rgba(0,212,255,0.3)'  },
  Withdrawn: { bg:'rgba(255,71,87,0.12)',  color:'#ff4757', border:'rgba(255,71,87,0.3)'  },
};

const STATUS_ORDER: Record<string, number> = { Upcoming:0, Pipeline:1, Completed:2, Withdrawn:3 };

export class IPOCalendarTab implements Tab {
  readonly id = 'ipo';

  private statusFilter = 'All';
  private pendingSave:  ((data: Record<string, string>) => Promise<void>) | null = null;

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }

  init(): void {
    this.el('ipo-sync-btn').addEventListener('click', () => this.sync());
    this.el('ipo-add-btn').addEventListener('click',  () => this.openAdd());

    (this.el('ipo-filter') as HTMLInputElement).addEventListener('input', e =>
      this.renderTable((e.target as HTMLInputElement).value),
    );

    // Status filter buttons
    document.querySelectorAll<HTMLElement>('[data-ipo-status]').forEach(btn =>
      btn.addEventListener('click', () => this.setStatus(btn.dataset.ipoStatus ?? 'All')),
    );

    // Edit/delete via event delegation — no JSON in onclick attributes
    this.el('ipo-tbody').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!btn) return;
      const row = store.get('ipoData').find(r => String(r._id) === btn.dataset.id);
      if (!row) return;
      if (btn.dataset.action === 'edit')   this.openEdit(row as IpoRow);
      if (btn.dataset.action === 'delete') this.delete(row as IpoRow);
    });
  }

  render(): void {
    const cached = store.get('ipoData');
    if (cached.length) {
      this.renderTable();
      this.updateStats();
    } else {
      this.sync();
    }
  }

  private async sync(): Promise<void> {
    const btn = this.el('ipo-sync-btn') as HTMLButtonElement;
    btn.textContent = '⟳';
    btn.style.opacity = '0.6';
    try {
      const data = await fetchSheet<IpoRow>('IPO');
      if (data.status !== 'ok') throw new Error(data.message ?? 'Error loading IPO sheet');
      store.set('ipoData', data.rows ?? []);
      this.renderTable();
      this.updateStats();
      this.el('ipo-last-updated').textContent =
        `${(data.rows ?? []).length} entries · ${new Date().toLocaleTimeString()}`;
      showToast(`✓ Loaded ${(data.rows ?? []).length} IPO entries`);
    } catch (err: unknown) {
      showToast(`IPO load failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      btn.textContent = '↺ SYNC';
      btn.style.opacity = '1';
    }
  }

  private updateStats(): void {
    const rows = store.get('ipoData') as IpoRow[];
    const counts = { Completed: 0, Upcoming: 0, Pipeline: 0, Withdrawn: 0 };
    rows.forEach(r => { if (r.Status in counts) counts[r.Status as keyof typeof counts]++; });
    this.el('ipo-stat-completed').textContent = String(counts.Completed);
    this.el('ipo-stat-upcoming').textContent  = String(counts.Upcoming);
    this.el('ipo-stat-pipeline').textContent  = String(counts.Pipeline);
    this.el('ipo-stat-withdrawn').textContent = String(counts.Withdrawn);
    this.el('ipo-stat-total').textContent     = String(rows.length);
  }

  private setStatus(status: string): void {
    this.statusFilter = status;
    ['All','Completed','Upcoming','Pipeline','Withdrawn'].forEach(s => {
      const btn = document.getElementById(`ipo-status-${s.toLowerCase()}`) as HTMLElement | null;
      if (!btn) return;
      const active = s === status;
      btn.style.background   = active ? 'rgba(0,212,255,0.15)' : 'transparent';
      btn.style.borderColor  = active ? 'rgba(0,212,255,0.4)'  : 'var(--border)';
      btn.style.color        = active ? 'var(--accent)'         : 'var(--text3)';
    });
    this.renderTable();
  }

  private renderTable(query?: string): void {
    const q = (query ?? (this.el('ipo-filter') as HTMLInputElement).value ?? '').toLowerCase();
    let rows = (store.get('ipoData') as IpoRow[]).filter(r => {
      if (this.statusFilter !== 'All' && r.Status !== this.statusFilter) return false;
      if (q && !Object.values(r).join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => (STATUS_ORDER[a.Status] ?? 99) - (STATUS_ORDER[b.Status] ?? 99));

    setTableState('ipo-tbody', 'ipo-table-wrap', 'ipo-empty', rows, (row: IpoRow) => {
      const sc     = STATUS_COLORS[row.Status] ?? STATUS_COLORS['Pipeline'];
      const ticker = (row.Ticker ?? '').trim();
      const sectors = (row.Sector ?? '').split(',').map(s => s.trim()).filter(Boolean);
      const id     = String(row._id);

      return `<tr>
        <td><span style="background:${sc.bg};color:${sc.color};border:1px solid ${sc.border};padding:2px 8px;border-radius:2px;font-family:var(--mono);font-size:10px;font-weight:700;letter-spacing:0.08em;white-space:nowrap;">${esc(row.Status)}</span></td>
        <td><strong style="color:var(--text);">${esc(row.Company)}</strong></td>
        <td>${ticker ? `<span class="ticker-badge">${esc(ticker)}</span>` : '<span style="color:var(--text3);font-size:11px;">TBD</span>'}</td>
        <td class="text-muted">${esc(row.Exchange ?? 'TBD')}</td>
        <td>${sectors.map(s => `<span class="tag">${esc(s)}</span>`).join(' ') || '—'}</td>
        <td class="text-muted">${esc(row.ExpectedDate ?? 'TBD')}</td>
        <td class="${row.PriceRange && row.PriceRange !== 'TBD' ? 'yes' : 'text-dim'}">${esc(row.PriceRange ?? 'TBD')}</td>
        <td class="text-muted">${esc(row.Shares ?? 'TBD')}</td>
        <td class="text-muted">${esc(row.Valuation ?? 'TBD')}</td>
        <td class="notes-cell">${esc(row.Notes ?? '—')}</td>
        <td><div style="display:flex;gap:4px;">
          <button data-action="edit"   data-id="${id}" style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">✏️</button>
          <button data-action="delete" data-id="${id}" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">🗑️</button>
        </div></td>
      </tr>`;
    });
  }

  // ── CRUD ───────────────────────────────────────────────────────────────────

  private openAdd(): void {
    this.showModal('➕ Add IPO Entry', async data => {
      const ok = await writeToSheet('append', 'IPO', data);
      if (ok) { showToast(`✓ ${data['Company']} added!`); setTimeout(() => this.sync(), 1000); }
    });
  }

  private openEdit(row: IpoRow): void {
    this.showModal(`✏️ Edit IPO — ${row.Company}`, async data => {
      const ok = await writeToSheet('update', 'IPO', { ...data, _id: row._id });
      if (ok) { showToast(`✓ ${data['Company']} updated!`); setTimeout(() => this.sync(), 1000); }
    }, row as unknown as Record<string, string>);
  }

  private async delete(row: IpoRow): Promise<void> {
    if (!confirm(`Delete ${row.Company} from IPO calendar?`)) return;
    const ok = await writeToSheet('delete', 'IPO', { _id: row._id });
    if (ok) { showToast(`✓ ${row.Company} deleted`); setTimeout(() => this.sync(), 800); }
  }

  // ── Modal ─────────────────────────────────────────────────────────────────

  private showModal(
    title: string,
    onSave: (data: Record<string, string>) => Promise<void>,
    prefill: Record<string, string> = {},
  ): void {
    document.getElementById('ipo-modal')?.remove();
    this.pendingSave = onSave;

    const modal = document.createElement('div');
    modal.id = 'ipo-modal';
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;overflow-y:auto;';
    modal.innerHTML = `
      <div style="background:var(--bg2);border:1px solid var(--border);border-radius:6px;padding:28px;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;">
        <div style="font-family:var(--sans);font-size:18px;font-weight:800;color:var(--text);margin-bottom:20px;">${esc(title)}</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          ${FIELDS.map(f => `
          <div style="${f.type === 'textarea' ? 'grid-column:1/-1;' : ''}">
            <label style="display:block;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:4px;">${esc(f.label)}${f.required ? ' *' : ''}</label>
            ${f.type === 'select'
              ? `<select id="ipo-field-${f.key}" style="width:100%;background:var(--panel);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:12px;padding:8px 10px;border-radius:3px;outline:none;">
                  ${(f.options ?? []).map(o => `<option value="${esc(o)}"${prefill[f.key] === o ? ' selected' : ''}>${esc(o)}</option>`).join('')}
                 </select>`
              : f.type === 'textarea'
              ? `<textarea id="ipo-field-${f.key}" rows="3" style="width:100%;background:var(--panel);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:12px;padding:8px 10px;border-radius:3px;outline:none;resize:vertical;">${esc(prefill[f.key] ?? '')}</textarea>`
              : `<input id="ipo-field-${f.key}" type="text" value="${esc(prefill[f.key] ?? '')}" style="width:100%;background:var(--panel);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:12px;padding:8px 10px;border-radius:3px;outline:none;">`
            }
          </div>`).join('')}
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:20px;">
          <button id="ipo-modal-cancel" style="background:none;border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:11px;padding:8px 16px;cursor:pointer;border-radius:3px;">CANCEL</button>
          <button id="ipo-modal-save" style="background:rgba(46,213,115,0.1);border:1px solid rgba(46,213,115,0.3);color:var(--green);font-family:var(--mono);font-size:11px;padding:8px 20px;cursor:pointer;border-radius:3px;letter-spacing:0.1em;">SAVE</button>
        </div>
      </div>`;

    document.body.appendChild(modal);
    modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
    modal.querySelector('#ipo-modal-cancel')!.addEventListener('click', () => modal.remove());
    modal.querySelector('#ipo-modal-save')!.addEventListener('click',   () => this.submitModal());
    setTimeout(() => (document.getElementById('ipo-field-Company') as HTMLInputElement | null)?.focus(), 100);
  }

  private async submitModal(): Promise<void> {
    if (!this.pendingSave) return;
    const formData: Record<string, string> = {};
    FIELDS.forEach(f => {
      const el = document.getElementById(`ipo-field-${f.key}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (el) formData[f.key] = el.value.trim();
    });
    if (!formData['Company']) { showToast('Company name is required', 'warn'); return; }
    document.getElementById('ipo-modal')?.remove();
    await this.pendingSave(formData);
    this.pendingSave = null;
  }
}
